import { useState } from "react";
import {
  AI_ENGINES,
  AI_MODEL_OPTIONS,
  AI_ROLES,
  type AiInvocationRow,
  type AiModelRow,
} from "../lib/aiModels";
import { invokeAdmin, errMsg } from "../lib/adminApi";
import { useAdminQuery } from "../hooks/useAdminQuery";
import { Banner } from "../components/ui";

interface ModelsResponse {
  models: AiModelRow[];
  invocations: AiInvocationRow[];
}
interface Health {
  events: number;
  places: number;
}
interface Quality {
  impressions: number;
  zero_result_rate: number;
  avg_result_count: number;
  engagements_matched: number;
  saved_position: { top3: number; top4_10: number; beyond10: number };
}

interface Bundle {
  models: ModelsResponse;
  health: Health | null;
  ranking: Record<string, number> | null;
  quality: Quality | null;
}

export default function AiModelsPage() {
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Form state for activating a model.
  const [role, setRole] = useState<string>("heavy");
  const [modelIdx, setModelIdx] = useState<number>(0);
  const [engine, setEngine] = useState<string>("langgraph");
  const [maxTokens, setMaxTokens] = useState<string>("8192");

  // Single cancellation-safe fetch with a real loading state + Refresh (ADM-5,
  // ADM-6). The models call is required; the other three degrade independently.
  const { data: bundle, loading, error, refetch } = useAdminQuery<Bundle>(async () => {
    const [m, hResult, rResult, qResult] = await Promise.allSettled([
      invokeAdmin<ModelsResponse>("admin_ai_models_get", {}),
      invokeAdmin<Health>("admin_embedding_health_get", {}),
      invokeAdmin<Record<string, number>>("public_ranking_config", {}),
      invokeAdmin<Quality>("admin_search_quality", {}),
    ]);
    if (m.status !== "fulfilled") throw new Error(errMsg(m.reason));
    return {
      models: m.value,
      health: hResult.status === "fulfilled" ? hResult.value : null,
      ranking: rResult.status === "fulfilled" ? rResult.value : null,
      quality: qResult.status === "fulfilled" ? qResult.value : null,
    };
  }, []);

  const data = bundle?.models ?? null;
  const health = bundle?.health ?? null;
  const ranking = bundle?.ranking ?? null;
  const quality = bundle?.quality ?? null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const option = AI_MODEL_OPTIONS[modelIdx];

    // Validate maxTokens before sending — `Number("abc")` is NaN and would have
    // been posted verbatim to admin_ai_model_set (ADM-6).
    let params: Record<string, unknown> = {};
    if (maxTokens.trim()) {
      const parsed = Number(maxTokens);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        setSubmitError("maxTokens must be a positive number.");
        return;
      }
      params = { maxTokens: parsed };
    }

    // Confirm the swap showing current → new — Activate flips the LIVE prod
    // extraction model within ~60s (ADM-4).
    const current = data?.models.find((m) => m.role === role);
    const currentLabel = current ? `${current.provider} / ${current.model_id}` : "(none)";
    if (
      !window.confirm(
        `Activate for role "${role}"?\n\n` +
          `current: ${currentLabel}\n` +
          `new:     ${option.provider} / ${option.modelId}\n\n` +
          `This changes the live production model within ~60s.`,
      )
    )
      return;

    setBusy(true);
    setSubmitError(null);
    try {
      await invokeAdmin("admin_ai_model_set", {
        role,
        provider: option.provider,
        model_id: option.modelId,
        engine: role === "embedding" ? null : engine,
        params,
      });
      refetch();
    } catch (err) {
      setSubmitError(errMsg(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">AI / Models</h1>
          <p className="mt-1 text-sm text-zinc-400">
            Active extraction model per role. Changes take effect within ~60s (the
            Lambda registry cache TTL). The live default stays until a winning eval
            scorecard justifies a flip.
          </p>
        </div>
        <button
          type="button"
          onClick={refetch}
          disabled={loading}
          className="shrink-0 rounded bg-zinc-700 px-3 py-1.5 text-sm text-white hover:bg-zinc-600 disabled:opacity-50"
        >
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {error && <Banner className="mt-4">{error}</Banner>}
      {submitError && <Banner className="mt-4">{submitError}</Banner>}
      {loading && !bundle && (
        <p className="mt-6 text-sm text-zinc-500">Loading models…</p>
      )}

      {health && (
        <div className="mt-4 rounded border border-zinc-800 bg-zinc-950 p-3 text-sm">
          <span className="text-zinc-400">Embeddings missing — </span>
          <span className={health.events ? "text-amber-400" : "text-zinc-300"}>
            events: {health.events}
          </span>
          <span className="text-zinc-600"> · </span>
          <span className={health.places ? "text-amber-400" : "text-zinc-300"}>
            places: {health.places}
          </span>
          <span className="text-zinc-500"> (backfill runs every 15m)</span>
        </div>
      )}

      <h2 className="mt-6 text-sm font-medium text-zinc-300">Active models</h2>
      <table className="mt-2 w-full text-left text-sm">
        <thead className="text-zinc-500">
          <tr>
            <th className="py-1">role</th>
            <th>provider</th>
            <th>model</th>
            <th>engine</th>
            <th>v</th>
          </tr>
        </thead>
        <tbody>
          {(data?.models ?? []).map((m) => (
            <tr key={m.role} className="border-t border-zinc-900">
              <td className="py-1 font-medium">{m.role}</td>
              <td>{m.provider}</td>
              <td className="font-mono text-xs text-zinc-300">{m.model_id}</td>
              <td>{m.engine ?? "—"}</td>
              <td>{m.version}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2 className="mt-6 text-sm font-medium text-zinc-300">Change model</h2>
      <form onSubmit={submit} className="mt-2 flex flex-wrap items-end gap-3">
        <label className="text-xs text-zinc-400">
          role
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="mt-1 block rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-sm text-zinc-200"
          >
            {AI_ROLES.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
        </label>
        <label className="text-xs text-zinc-400">
          model
          <select
            value={modelIdx}
            onChange={(e) => setModelIdx(Number(e.target.value))}
            className="mt-1 block rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-sm text-zinc-200"
          >
            {AI_MODEL_OPTIONS.map((o, i) => (
              <option key={o.modelId} value={i}>{o.label}</option>
            ))}
          </select>
        </label>
        <label className="text-xs text-zinc-400">
          engine
          <select
            value={engine}
            onChange={(e) => setEngine(e.target.value)}
            disabled={role === "embedding"}
            className="mt-1 block rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-sm text-zinc-200 disabled:opacity-40"
          >
            {AI_ENGINES.map((en) => (
              <option key={en} value={en}>{en}</option>
            ))}
          </select>
        </label>
        <label className="text-xs text-zinc-400">
          maxTokens
          <input
            type="number"
            min={1}
            step={1}
            value={maxTokens}
            onChange={(e) => setMaxTokens(e.target.value)}
            className="mt-1 block w-24 rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-sm text-zinc-200"
          />
        </label>
        <button
          type="submit"
          disabled={busy}
          className="rounded bg-zinc-200 px-3 py-1 text-sm font-medium text-zinc-900 hover:bg-white disabled:opacity-50"
        >
          {busy ? "Saving…" : "Activate"}
        </button>
      </form>

      <h2 className="mt-6 text-sm font-medium text-zinc-300">Recent invocations</h2>
      <table className="mt-2 w-full text-left text-sm">
        <thead className="text-zinc-500">
          <tr>
            <th className="py-1">when</th>
            <th>role</th>
            <th>model</th>
            <th>ok</th>
            <th>ms</th>
            <th>$</th>
          </tr>
        </thead>
        <tbody>
          {(data?.invocations ?? []).map((iv, i) => (
            <tr key={i} className="border-t border-zinc-900">
              <td className="py-1 text-zinc-400">{new Date(iv.created_at).toLocaleString()}</td>
              <td>{iv.role ?? "—"}</td>
              <td className="font-mono text-xs text-zinc-300">{iv.model_id ?? "—"}</td>
              <td className={iv.valid === false ? "text-red-400" : "text-zinc-300"}>
                {iv.valid === false ? "no" : "yes"}
              </td>
              <td>{iv.latency_ms ?? "—"}</td>
              <td>{iv.est_cost_usd ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!data?.invocations?.length && (
        <p className="mt-2 text-xs text-zinc-500">
          No invocations recorded yet (the trace write is wired in a follow-up).
        </p>
      )}

      <h2 className="mt-6 text-sm font-medium text-zinc-300">Ranking config</h2>
      <p className="mt-1 text-xs text-zinc-500">
        Tunable search / feed / composite weights (admin.ranking_config), live —
        the SQL scorers + client composite read these. Read-only here; edit via SQL.
      </p>
      <table className="mt-2 w-full text-left text-sm">
        <thead className="text-zinc-500">
          <tr>
            <th className="py-1">key</th>
            <th>value</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(ranking ?? {}).map(([k, v]) => (
            <tr key={k} className="border-t border-zinc-900">
              <td className="py-1 font-mono text-xs text-zinc-300">{k}</td>
              <td>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!ranking && (
        <p className="mt-2 text-xs text-zinc-500">Ranking config unavailable.</p>
      )}

      <h2 className="mt-6 text-sm font-medium text-zinc-300">Search quality (last 7d)</h2>
      <p className="mt-1 text-xs text-zinc-500">
        Server-side funnel eval — impressions joined to saved/shared events.
        Good ranking ⇒ engaged events cluster in the top positions. (Near-empty
        until the beta has search traffic.)
      </p>
      {quality ? (
        <div className="mt-2 space-y-2 text-sm">
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-zinc-300">
            <span>impressions: <span className="font-mono">{quality.impressions}</span></span>
            <span>
              zero-result rate:{" "}
              <span className={quality.zero_result_rate > 0.2 ? "font-mono text-amber-400" : "font-mono"}>
                {(quality.zero_result_rate * 100).toFixed(1)}%
              </span>
            </span>
            <span>avg results: <span className="font-mono">{quality.avg_result_count}</span></span>
            <span>engagements matched: <span className="font-mono">{quality.engagements_matched}</span></span>
          </div>
          <div className="text-zinc-400">
            engaged-event displayed position —{" "}
            <span className="text-emerald-400">top 3: {quality.saved_position?.top3 ?? 0}</span>
            <span className="text-zinc-600"> · </span>
            <span>4–10: {quality.saved_position?.top4_10 ?? 0}</span>
            <span className="text-zinc-600"> · </span>
            <span className={quality.saved_position?.beyond10 ? "text-amber-400" : ""}>
              beyond 10: {quality.saved_position?.beyond10 ?? 0}
            </span>
          </div>
        </div>
      ) : (
        <p className="mt-2 text-xs text-zinc-500">Search-quality metrics unavailable.</p>
      )}
    </div>
  );
}
